import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const DAY_MAP = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6
};

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TZ_SHORT = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'Asia/Kolkata': 'IST'
};

// US holidays for current year
const getUSHolidays = (year) => {
  const holidays = [];
  // New Year's Day
  holidays.push({ date: new Date(year, 0, 1), name: "New Year's Day" });
  // MLK Day - 3rd Monday of January
  const jan1 = new Date(year, 0, 1);
  const mlkDay = new Date(year, 0, 1 + ((1 - jan1.getDay() + 7) % 7) + 14);
  holidays.push({ date: mlkDay, name: 'Martin Luther King Jr. Day' });
  // Presidents' Day - 3rd Monday of February
  const feb1 = new Date(year, 1, 1);
  const presDay = new Date(year, 1, 1 + ((1 - feb1.getDay() + 7) % 7) + 14);
  holidays.push({ date: presDay, name: "Presidents' Day" });
  // Memorial Day - Last Monday of May
  const may31 = new Date(year, 4, 31);
  const memDay = new Date(year, 4, 31 - ((may31.getDay() + 6) % 7));
  holidays.push({ date: memDay, name: 'Memorial Day' });
  // Independence Day
  holidays.push({ date: new Date(year, 6, 4), name: 'Independence Day' });
  // Labor Day - 1st Monday of September
  const sep1 = new Date(year, 8, 1);
  const laborDay = new Date(year, 8, 1 + ((1 - sep1.getDay() + 7) % 7));
  holidays.push({ date: laborDay, name: 'Labor Day' });
  // Thanksgiving - 4th Thursday of November
  const nov1 = new Date(year, 10, 1);
  const thanksgiving = new Date(year, 10, 1 + ((4 - nov1.getDay() + 7) % 7) + 21);
  holidays.push({ date: thanksgiving, name: 'Thanksgiving' });
  // Christmas
  holidays.push({ date: new Date(year, 11, 25), name: 'Christmas Day' });
  return holidays;
};

const Overview = () => {
  const { user, studio } = useAuth();
  const [stats, setStats] = useState({ courses: 0, activeStudents: 0 });
  const [courses, setCourses] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studioRes, coursesRes, attendanceRes] = await Promise.allSettled([
          api.get('/studio'),
          api.get('/courses'),
          api.get('/attendance/summary')
        ]);

        if (studioRes.status === 'fulfilled') {
          setStats(studioRes.value.data.stats || { courses: 0, activeStudents: 0 });
        }
        if (coursesRes.status === 'fulfilled') {
          setCourses((coursesRes.value.data.courses || []).filter(c => c.status === 'active'));
        }
        if (attendanceRes.status === 'fulfilled') {
          setAttendanceSummary(attendanceRes.value.data.summary || attendanceRes.value.data || []);
        }
      } catch (err) {
        console.error('Failed to load overview data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
        <span className="ml-3 text-gray-500">Loading...</span>
      </div>
    );
  }

  // This week's classes
  const now = new Date();
  const today = now.getDay();
  const thisWeekClasses = [];
  courses.forEach(course => {
    (course.schedule?.days || []).forEach(day => {
      const dayNum = DAY_MAP[day];
      if (dayNum === undefined) return;
      // Calculate the date for this day in the current week
      const diff = dayNum - today;
      const classDate = new Date(now);
      classDate.setDate(now.getDate() + diff);
      classDate.setHours(0, 0, 0, 0);

      // Only show today and future days this week
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      if (classDate >= todayStart && diff <= 6) {
        const isToday = diff === 0;
        const isTomorrow = diff === 1;
        const tz = TZ_SHORT[course.schedule?.timezone] || '';
        thisWeekClasses.push({
          courseId: course._id,
          courseName: course.name,
          levelName: course.levelName,
          dayLabel: isToday ? 'Today' : isTomorrow ? 'Tomorrow' : DAY_LABELS[dayNum],
          time: `${course.schedule?.startTime || ''} - ${course.schedule?.endTime || ''}`.trim(),
          tz,
          enrolledCount: course.enrolledCount || 0,
          isToday,
          dayNum
        });
      }
    });
  });
  thisWeekClasses.sort((a, b) => a.dayNum - b.dayNum);

  // Attendance by offering (from /attendance/summary which returns courseId, presentCount, totalRecords, attendanceRate)
  const attendanceByOffering = attendanceSummary.map(s => ({
    name: s.courseName,
    rate: s.attendanceRate,
    present: s.presentCount || 0,
    total: s.totalRecords || 0,
    enrolledCount: s.enrolledCount || 0
  })).filter(c => c.total > 0)
    .sort((a, b) => (b.rate || 0) - (a.rate || 0));

  // Upcoming holidays (studio + US)
  const year = now.getFullYear();
  const studioHolidays = (studio?.holidays || []).map(h => ({
    date: new Date(h.date),
    name: h.name,
    type: 'studio'
  }));
  const usHolidays = getUSHolidays(year).map(h => ({ ...h, type: 'us' }));
  const allHolidays = [...studioHolidays, ...usHolidays]
    .filter(h => h.date >= now)
    .sort((a, b) => a.date - b.date)
    .slice(0, 5);

  const stripeConnected = studio?.stripeConnectStatus === 'active';

  return (
    <div className="flex gap-8">
      {/* Main Column */}
      <div className="flex-1 min-w-0">

        {/* Stripe Connect Setup Banner */}
        {!stripeConnected && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
              <div>
                <h3 className="text-sm font-semibold text-amber-800 mb-1">Complete your studio setup</h3>
                <p className="text-sm text-amber-700 mb-3">
                  Connect your Stripe account to start accepting payments and creating offerings.
                </p>
                <Link
                  to="/dashboard/settings?tab=integrations"
                  className="inline-flex items-center gap-1.5 bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                  Connect Stripe in Settings
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="hidden sm:grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
            <div className="text-3xl font-bold text-gray-900">{stats.courses}</div>
            <div className="text-sm text-gray-500 mt-1">Offerings</div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
            <div className="text-3xl font-bold text-gray-900">{stats.activeStudents}</div>
            <div className="text-sm text-gray-500 mt-1">Active Students</div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
            <div className="text-3xl font-bold text-gray-900">--</div>
            <div className="text-sm text-gray-500 mt-1">Revenue This Month</div>
          </div>
        </div>

        {/* This Week's Classes */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">This Week's Classes</h2>
          {thisWeekClasses.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 text-center text-gray-500">
              No upcoming classes this week
            </div>
          ) : (
            <div className="space-y-2">
              {thisWeekClasses.map((cls, i) => (
                <div
                  key={i}
                  className={`bg-white rounded-xl border shadow-sm p-4 flex items-center justify-between ${
                    cls.isToday ? 'border-brand-200 bg-brand-50' : 'border-gray-100'
                  }`}
                >
                  <div className="min-w-0">
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${cls.isToday ? 'text-brand-600' : 'text-gray-400'}`}>
                      {cls.dayLabel}
                    </p>
                    <p className="text-sm font-medium text-gray-900">{cls.courseName}</p>
                    <p className="text-xs text-gray-500">{cls.time} {cls.tz}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-gray-400">{cls.enrolledCount} students</span>
                    {cls.isToday && (
                      <Link
                        to="/dashboard/students?tab=attendance"
                        className="text-xs font-medium text-brand-500 hover:text-brand-600"
                      >
                        Mark Attendance
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Attendance by Offering */}
        {attendanceByOffering.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Attendance by Offering</h2>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
              {attendanceByOffering.slice(0, 5).map((course, i) => (
                <div key={i} className="p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{course.name}</p>
                    <p className="text-xs text-gray-400">{course.enrolledCount} students</p>
                  </div>
                  <div className="w-32 flex-shrink-0">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`rounded-full h-2 transition-all ${
                          course.rate >= 80 ? 'bg-green-500' :
                          course.rate >= 60 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${course.rate || 0}%` }}
                      />
                    </div>
                  </div>
                  <span className={`text-sm font-semibold w-12 text-right ${
                    course.rate >= 80 ? 'text-green-600' :
                    course.rate >= 60 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {course.rate}%
                  </span>
                </div>
              ))}
            </div>
            {attendanceByOffering.length > 5 && (
              <Link
                to="/dashboard/students?tab=attendance"
                className="inline-block text-sm text-brand-500 hover:text-brand-600 font-medium mt-3"
              >
                View all {attendanceByOffering.length} offerings →
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Right Sidebar */}
      <div className="w-72 flex-shrink-0 space-y-5 hidden lg:block">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Actions</h3>
          <div className="space-y-2">
            <Link
              to="/dashboard/courses/new"
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-brand-500 transition-colors py-1"
            >
              <svg className="w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              New Offering
            </Link>
            <Link
              to="/dashboard/students?tab=attendance"
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-brand-500 transition-colors py-1"
            >
              <svg className="w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              Mark Attendance
            </Link>
            <Link
              to="/dashboard/students"
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-brand-500 transition-colors py-1"
            >
              <svg className="w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
              </svg>
              View Students
            </Link>
            <Link
              to="/dashboard/settings"
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-brand-500 transition-colors py-1"
            >
              <svg className="w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
              </svg>
              Studio Settings
            </Link>
          </div>
        </div>

        {/* Upcoming Holidays */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Upcoming Holidays</h3>
          {allHolidays.length > 0 ? (
            <div className="space-y-2.5">
              {allHolidays.map((h, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    h.type === 'studio' ? 'bg-brand-500' : 'bg-orange-400'
                  }`} />
                  <div>
                    <p className="text-sm text-gray-700">{h.name}</p>
                    <p className="text-xs text-gray-400">
                      {h.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {h.type === 'studio' && ' — Studio closed'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No upcoming holidays</p>
          )}
          <Link
            to="/dashboard/calendar"
            className="text-xs text-brand-500 hover:text-brand-600 font-medium mt-3 inline-block"
          >
            View Calendar
          </Link>
        </div>

        {/* Announcements (Placeholder) */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Announcements</h3>
            <span className="text-xs text-gray-400">Coming soon</span>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 py-2 px-3 bg-gray-50 rounded-lg opacity-60">
              <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Birthday Wishes</p>
                <p className="text-xs text-gray-400">Auto-send to students</p>
              </div>
            </div>
            <div className="flex items-center gap-3 py-2 px-3 bg-gray-50 rounded-lg opacity-60">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Holiday Notices</p>
                <p className="text-xs text-gray-400">Notify class cancellations</p>
              </div>
            </div>
            <div className="flex items-center gap-3 py-2 px-3 bg-gray-50 rounded-lg opacity-60">
              <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">General Updates</p>
                <p className="text-xs text-gray-400">Broadcast to all students</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
